import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import NextLink, { LinkProps } from 'next/link'
import { NextRouter, useRouter } from 'next/router'
import { useRouter as useNprogressRouter } from 'next-nprogress-bar'
import { forwardRef, PropsWithChildren } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { getUpdatedLocaleForURLAfterRegionSwitch as getUpdatedLocaleForURL } from '@src/components/locale/utils'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'

const LinkI18N = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(function LinkI18N(
  { onClick, href, ...restProps },
  ref,
) {
  const { locale, regionDetails } = useLocaleContext()
  const updatedLocaleForURL = getUpdatedLocaleForURL(locale, regionDetails)
  return (
    <NextLink {...restProps} ref={ref} href={href} prefetch={false} onClick={onClick} locale={updatedLocaleForURL} />
  )
})

const LinkWithoutI18n = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(function LinkWithoutI18n(
  { href, ...restProps },
  ref,
) {
  return <NextLink ref={ref} href={href} {...restProps} prefetch={false} />
})

export const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>((props, ref) => {
  const isI18nPreviewEnabled = useI18nPreviewContext()
  return isI18nPreviewEnabled ? <LinkI18N {...props} ref={ref} /> : <LinkWithoutI18n {...props} ref={ref} />
})

// @i18n-ignore
Link.displayName = 'CustomLink'

export const useQuinceNprogressRouter = () => {
  const router = useNprogressRouter()
  const push = (to: string, options?: NavigateOptions) => {
    router.push(to, options)
  }

  const replace = (to: string, options?: NavigateOptions) => {
    router.replace(to, options)
  }

  return {
    ...router,
    push,
    replace,
  }
}

export interface QuinceRouter extends Omit<NextRouter, 'push' | 'replace'> {
  push: (to: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
  replace: (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
}

export const useQuinceRouter: () => QuinceRouter = () => {
  const router = useRouter()

  const push = (to: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    void router.push(to, to, options)
  }

  const replace = (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    void router.replace(to, as ?? to, options) // as is optional in next/router
  }

  return {
    ...router,
    push,
    replace,
  }
}
